import { isBranching, isTerminal, } from './types.js';
/**
 * Pruefung und Navigation im Journey-Graphen.
 *
 * Reine Funktionen: kein Datenbankzugriff, keine Zeit, keine Nebenwirkungen.
 * Der Worker fragt hier nur, WELCHER Knoten als naechstes dran ist und WIE
 * LANGE gewartet wird — was dabei tatsaechlich passiert, entscheidet er
 * selbst.
 */
const MAX_NODES = 200;
export function findNode(graph, nodeId) {
    return graph.nodes.find((node) => node.id === nodeId);
}
export function entryNode(graph) {
    return graph.nodes.find((node) => node.type === 'trigger');
}
/**
 * Naechster Knoten entlang eines Zweigs.
 *
 * Gibt es keine passende Kante, endet der Durchlauf. Das ist Absicht: ein
 * Zweig ohne Fortsetzung bedeutet "hier ist Schluss", nicht "Fehler". Wer
 * einen Ast bewusst enden lassen will, muss keinen Exit-Knoten anhaengen.
 */
export function nextNode(graph, fromId, branch) {
    const edge = graph.edges.find((e) => e.from === fromId && e.branch === branch);
    if (!edge)
        return undefined;
    return findNode(graph, edge.to);
}
export function durationToMs(duration) {
    const factor = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 }[duration.unit];
    return Math.max(0, Math.trunc(duration.value)) * factor;
}
/**
 * Prueft einen Graphen vor dem Scharfschalten.
 *
 * Alle Probleme werden gesammelt, nicht nur das erste — sonst muesste der
 * Nutzer den Graphen in mehreren Runden reparieren.
 */
export function validateGraph(graph) {
    const problems = [];
    const nodes = graph.nodes ?? [];
    const edges = graph.edges ?? [];
    if (nodes.length === 0) {
        return [{ message: 'Der Ablauf enthält keine Knoten.' }];
    }
    if (nodes.length > MAX_NODES) {
        problems.push({ message: `Mehr als ${MAX_NODES} Knoten werden nicht unterstützt.` });
    }
    const ids = new Set();
    for (const node of nodes) {
        if (ids.has(node.id)) {
            problems.push({
                nodeId: node.id,
                message: `Die Knotenkennung "${node.id}" kommt mehrfach vor.`,
            });
        }
        ids.add(node.id);
    }
    const triggers = nodes.filter((node) => node.type === 'trigger');
    if (triggers.length === 0) {
        problems.push({ message: 'Es fehlt ein Auslöser — ohne ihn betritt niemand den Ablauf.' });
    }
    if (triggers.length > 1) {
        problems.push({
            message: `Es gibt ${triggers.length} Auslöser. Ein Ablauf hat genau einen Einstieg.`,
        });
    }
    for (const edge of edges) {
        if (!ids.has(edge.from)) {
            problems.push({ message: `Kante geht von einem unbekannten Knoten aus: ${edge.from}` });
        }
        if (!ids.has(edge.to)) {
            problems.push({ nodeId: edge.from, message: `Kante zeigt ins Leere: ${edge.to}` });
        }
        if (edge.from === edge.to) {
            problems.push({
                nodeId: edge.from,
                message: 'Ein Knoten darf nicht auf sich selbst zeigen.',
            });
        }
    }
    problems.push(...checkBranches(nodes, edges));
    problems.push(...checkReachability(graph, triggers[0]));
    problems.push(...checkCycles(graph));
    return problems;
}
function checkBranches(nodes, edges) {
    const problems = [];
    for (const node of nodes) {
        const outgoing = edges.filter((e) => e.from === node.id);
        if (isTerminal(node)) {
            if (outgoing.length > 0) {
                problems.push({ nodeId: node.id, message: 'Nach einem Ende kann nichts mehr folgen.' });
            }
            continue;
        }
        if (isBranching(node)) {
            const branches = new Set(outgoing.map((e) => e.branch));
            if (!branches.has('yes') && !branches.has('no')) {
                problems.push({
                    nodeId: node.id,
                    message: 'Eine Verzweigung braucht mindestens einen Ja- oder Nein-Zweig.',
                });
            }
            if (branches.has('next')) {
                problems.push({
                    nodeId: node.id,
                    message: 'Eine Verzweigung hat Ja/Nein-Zweige, keinen einfachen Nachfolger.',
                });
            }
            if (node.type === 'wait_until' && !branches.has('timeout')) {
                problems.push({
                    nodeId: node.id,
                    message: 'Ohne Zeitüberschreitungs-Zweig bleiben Kontakte hier hängen.',
                });
            }
            continue;
        }
        for (const branch of ['yes', 'no', 'timeout']) {
            if (outgoing.some((e) => e.branch === branch)) {
                problems.push({
                    nodeId: node.id,
                    message: `Dieser Knoten verzweigt nicht, hat aber einen "${branch}"-Zweig.`,
                });
            }
        }
        if (outgoing.filter((e) => e.branch === 'next').length > 1) {
            problems.push({ nodeId: node.id, message: 'Mehr als ein Nachfolger ist nicht eindeutig.' });
        }
    }
    return problems;
}
/**
 * Nicht erreichbare Knoten sind fast immer ein Versehen — ein abgehaengter
 * Zweig, den der Nutzer fuer aktiv haelt. Genau der Fehler, der im Altsystem
 * unbemerkt blieb.
 */
function checkReachability(graph, trigger) {
    if (!trigger)
        return [];
    const reachable = new Set([trigger.id]);
    const queue = [trigger.id];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const edge of graph.edges.filter((e) => e.from === current)) {
            if (!reachable.has(edge.to)) {
                reachable.add(edge.to);
                queue.push(edge.to);
            }
        }
    }
    return graph.nodes
        .filter((node) => !reachable.has(node.id))
        .map((node) => ({
        nodeId: node.id,
        message: 'Dieser Knoten ist vom Auslöser aus nicht erreichbar.',
    }));
}
/**
 * Zyklen ohne Wartezeit.
 *
 * Ein Kreis mit Wartezeit darin ist zulaessig — daraus baut man
 * wiederkehrende Abläufe. Ein Kreis OHNE Wartezeit wuerde den Worker in einer
 * Endlosschleife drehen lassen, bis der Prozess steht.
 */
function checkCycles(graph) {
    const problems = [];
    const state = new Map();
    const walk = (nodeId, path) => {
        const status = state.get(nodeId);
        if (status === 'done')
            return;
        if (status === 'open') {
            const start = path.indexOf(nodeId);
            const cycle = path.slice(start);
            const hasWait = cycle.some((id) => {
                const node = findNode(graph, id);
                return node?.type === 'wait' || node?.type === 'wait_until';
            });
            if (!hasWait) {
                problems.push({
                    nodeId,
                    message: `Kreis ohne Wartezeit: ${[...cycle, nodeId].join(' → ')}. Ohne Pause dreht sich der Ablauf endlos.`,
                });
            }
            return;
        }
        state.set(nodeId, 'open');
        for (const edge of graph.edges.filter((e) => e.from === nodeId)) {
            walk(edge.to, [...path, nodeId]);
        }
        state.set(nodeId, 'done');
    };
    const start = entryNode(graph);
    if (start)
        walk(start.id, []);
    return problems;
}
/** Kurzfassung fuer Log und Oberflaeche. */
export function describeNodeType(node) {
    switch (node.type) {
        case 'trigger':
            return 'Auslöser';
        case 'send_email':
            return `E-Mail „${node.subject}"`;
        case 'wait':
            return `${node.duration.value} ${node.duration.unit === 'days' ? 'Tage' : node.duration.unit === 'hours' ? 'Stunden' : 'Minuten'} warten`;
        case 'condition':
            return 'Wenn/Dann';
        case 'wait_until':
            return 'Warten bis Bedingung erfüllt';
        case 'add_tag':
            return 'Tag setzen';
        case 'remove_tag':
            return 'Tag entfernen';
        case 'add_to_list':
            return 'Zu Liste hinzufügen';
        case 'remove_from_list':
            return 'Aus Liste entfernen';
        case 'set_score':
            return `Lead-Score ${node.delta >= 0 ? '+' : ''}${node.delta}`;
        case 'create_task':
            return `Aufgabe „${node.title}"`;
        case 'exit':
            return 'Ablauf beenden';
    }
}
//# sourceMappingURL=graph.js.map